import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import * as userActions from "../../../redux/reducers/userReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { hasConnectedStripe, STRIPE_SETUP_ROUTE } from "../../../utils/onboarding";
import { clubSiteBase, DEFAULT_LOOK, mergeLook } from "../../../utils/webWidgets";
import { describe, FREE_TRIAL_NAME } from "../../classes/products/productUtils";
import { Toggle } from "../accountSettings/shared";

/**
 * Website & webshop (2026-11-01), route /website. The club designs the blocks for its own website — Try a class,
 * Join the club, Buy credits — with a live preview (the real public page in ?embed=1&preview=1, the unsaved look sent
 * with postMessage "fitclub:preview"), then copies a snippet (public/embed.js). Saved with POST saveWebWidgets; the
 * blocks on the website pick up a saved look without pasting the snippet again.
 */

const BLOCKS = [
  { key: "trial", label: "Try a class", sub: "Free trial, no account needed", page: "trial", inline: true },
  { key: "join", label: "Join the club", sub: "Sign up as a member", page: "members/signup", inline: false },
  { key: "shop", label: "Buy credits", sub: "Your passes & subscriptions", page: "shop", inline: true },
];
const PRESETS = ["#481B3A", "#1F3D36", "#2563EB", "#111827", "#B4232F", "#D97706"];
const CORNERS = [["square", "Square"], ["rounded", "Rounded"], ["pill", "Pill"]];
const FONTS = [["site", "Same as my website"], ["mulish", "Mulish (FitClub)"], ["inter", "Inter"], ["serif", "Classic serif"]];
const LANGS = [["auto", "Visitor's language"], ["en", "English"], ["nl", "Nederlands"]];
const DEFAULT_LABEL = { shop: "Buy / Subscribe", trial: "Try a class", join: "Join the club" };

const sellable = (p) =>
  p && p.isDeleted !== true && p.availableForSale !== false && ["classpass", "subscription"].includes(p.productType)
  && String(p.productName || "").trim().toLowerCase() !== FREE_TRIAL_NAME && (parseFloat(p.price) || 0) > 0;

const Seg = ({ value, options, onChange, small = false }) => (
  <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
    {options.map(([v, label]) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        aria-pressed={value === v}
        className={classNames(
          "rounded-xl border px-2 text-center font-extrabold transition-colors",
          small ? "py-1.5 text-xs" : "py-2 text-[13px]",
          value === v ? "border-[#481B3A] bg-[#F7F1F6] text-[#481B3A] ring-1 ring-[#481B3A]" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
        )}
      >
        {label}
      </button>
    ))}
  </div>
);

const Row = ({ label, children, hint }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[13px] font-extrabold text-gray-800">{label}</span>
    {children}
    {hint && <span className="text-xs text-gray-500">{hint}</span>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-4 border-t border-gray-100 pt-5 first:border-0 first:pt-0">
    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-gray-500">{title}</p>
    {children}
  </div>
);

const Check = ({ checked, onChange, children }) => (
  <label className="flex cursor-pointer items-center justify-between gap-3 text-sm text-gray-800">
    <span>{children}</span>
    <Toggle checked={checked} onChange={onChange} label={typeof children === "string" ? children : ""} />
  </label>
);

export default function WebsiteWidgetsPage() {
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userData = useSelector(userState).userData || {};
  const clubLink = userData.myBookingLink || "";
  const savedLook = React.useMemo(() => mergeLook(userData?.webWidgets?.look), [userData?.webWidgets?.look]);
  const [look, setLook] = React.useState(savedLook);
  const [block, setBlock] = React.useState("shop");
  const [device, setDevice] = React.useState("desktop");
  const [step, setStep] = React.useState("products");
  const [snippetMode, setSnippetMode] = React.useState("inline");
  const [products, setProducts] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [frameHeight, setFrameHeight] = React.useState(560);
  const [hexText, setHexText] = React.useState(savedLook.brandColor);
  const frameRef = React.useRef(null);

  React.useEffect(() => setLook(savedLook), [savedLook]);
  React.useEffect(() => setHexText(look.brandColor), [look.brandColor]);
  const dirty = JSON.stringify(look) !== JSON.stringify(savedLook);
  const set = (patch) => setLook((l) => ({ ...l, ...patch }));
  const setLabel = (k, v) => setLook((l) => ({ ...l, labels: { ...l.labels, [k]: v.slice(0, 24) } }));

  // the club's products (for show / order / highlight)
  React.useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?getall=true` });
        const list = res?.data?.statusCode === 200 && Array.isArray(res.data.data) ? res.data.data : [];
        setProducts(list.filter(sellable));
      } catch (e) {
        setProducts([]);
      }
    })();
  }, []);

  const ordered = React.useMemo(() => {
    const list = products || [];
    const order = look.productOrder || [];
    const rank = (p) => {
      const i = order.indexOf(String(p._id));
      return i < 0 ? 1e6 : i;
    };
    return [...list].sort((a, b) => rank(a) - rank(b));
  }, [products, look.productOrder]);

  const move = (id, dir) => {
    const ids = ordered.map((p) => String(p._id));
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    set({ productOrder: ids });
  };
  const toggleShown = (id) => {
    const hidden = new Set(look.hiddenProducts || []);
    if (hidden.has(id)) hidden.delete(id);
    else hidden.add(id);
    set({ hiddenProducts: [...hidden], ...(look.highlightProductId === id && hidden.has(id) ? { highlightProductId: "" } : {}) });
  };

  /* ---------- live preview ---------- */
  const current = BLOCKS.find((b) => b.key === block);
  const previewSrc = React.useMemo(() => {
    const base = `${window.location.origin}/`;
    const q = `club=${encodeURIComponent(clubLink)}&embed=1&preview=1`;
    if (block === "join") return `${base}embed/button?kind=join&${q}`;
    return `${base}${current.page}?${q}`;
  }, [block, clubLink, current.page]);

  const postLook = React.useCallback(() => {
    const win = frameRef.current?.contentWindow;
    if (win) win.postMessage({ type: "fitclub:preview", look, step }, window.location.origin);
  }, [look, step]);

  React.useEffect(() => {
    postLook();
  }, [postLook]);

  React.useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || e.source !== frameRef.current?.contentWindow) return;
      if (e.data?.type === "fitclub:preview-ready") postLook();
      if (e.data?.type === "fitclub:height" && e.data.height) setFrameHeight(Math.max(120, Math.min(2400, e.data.height)));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [postLook]);

  /* ---------- snippet ---------- */
  const site = clubSiteBase(clubLink);
  const blockMode = !current.inline ? "button" : snippetMode === "button" ? "button" : "inline";
  const snippet =
    snippetMode === "link"
      ? `${site}${current.page}`
      : `<div data-fitclub="${block}" data-club="${clubLink}"${blockMode === "button" && current.inline ? ' data-mode="button"' : ""}></div>\n<script src="${site}embed.js" async></script>`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      addSuccessToast(snippetMode === "link" ? "Link copied" : "Code copied");
    } catch (e) {
      addErrorToast("Couldn't copy. Select the code and copy it yourself.");
    }
  };
  const mailBody = `Hi,\n\nCould you add this to our website? It shows "${current.label}" from FitClub.\nPaste it where it should appear, in an "Embed code" or "Custom HTML" block (Wix, WordPress, Squarespace, Webflow…).\n\n${snippet}\n\nThank you!`;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}saveWebWidgets`, data: { look } });
      if (res?.data?.statusCode === 200) {
        dispatch(userActions.setUserData({ ...userData, webWidgets: { ...(userData.webWidgets || {}), look: res.data.data.look } }));
        addSuccessToast("Look saved. Blocks on your website show it straight away.");
      } else addErrorToast(res?.data?.message || "Couldn't save. Please try again.");
    } catch (e) {
      addErrorToast("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const paymentsReady = hasConnectedStripe(userData);

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-[28px] font-black tracking-tight text-gray-900">Website &amp; webshop</h1>
              <p className="mt-1 max-w-2xl text-[15px] text-gray-600">
                Put FitClub on your own website. Design the blocks here; the preview shows exactly what visitors see.
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" disabled={!dirty || saving} onClick={() => setLook(savedLook)} className="h-11 rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-50">
                Discard
              </button>
              <button type="button" disabled={!dirty || saving} onClick={save} className="inline-flex h-11 items-center gap-2 rounded-2xl bg-brown-coffee px-5 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:bg-gray-300">
                {saving && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                Save look
              </button>
            </div>
          </div>

          {!clubLink ? (
            <p className="mt-6 rounded-3xl border border-gray-200 bg-white p-6 text-gray-700">
              Your club needs a club link first (Account settings → Club). The blocks live on that address.
            </p>
          ) : (
            <>
              <div className="mt-5 flex w-max max-w-full gap-1 overflow-x-auto rounded-full bg-[#EDE5F0] p-1">
                {BLOCKS.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => {
                      setBlock(b.key);
                      setStep("products");
                      if (!b.inline && snippetMode === "inline") setSnippetMode("button");
                    }}
                    className={classNames("whitespace-nowrap rounded-full px-4 py-2 text-sm font-extrabold", block === b.key ? "bg-white text-gray-900 shadow" : "text-gray-600")}
                  >
                    {b.label}
                  </button>
                ))}
              </div>

              {block === "shop" && !paymentsReady && (
                <div className="mt-4 rounded-2xl border border-[#F4D8A8] bg-[#FFF8EC] px-4 py-3 text-sm text-[#7A4A00]">
                  Set up payments first. Until then the shop on your website says you aren't selling online yet.{" "}
                  <a href={STRIPE_SETUP_ROUTE} className="font-extrabold underline">Set up payments</a>
                </div>
              )}

              <div className="mt-5 grid grid-cols-1 gap-6 xl:grid-cols-[340px_minmax(0,1fr)]">
                {/* ---------- controls ---------- */}
                <div className="flex flex-col gap-5 self-start rounded-3xl border border-gray-200 bg-white p-5">
                  <Section title="Colours">
                    <Row label="Brand colour" hint="Buttons, highlights and the payment page.">
                      <div className="flex flex-wrap items-center gap-2">
                        {PRESETS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            aria-label={`Colour ${c}`}
                            onClick={() => set({ brandColor: c })}
                            className={classNames("h-8 w-8 rounded-full", look.brandColor.toUpperCase() === c ? "ring-2 ring-offset-2" : "")}
                            style={{ background: c, "--tw-ring-color": c }}
                          />
                        ))}
                        <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-2 py-1">
                          <input type="color" aria-label="Pick a colour" value={look.brandColor} onChange={(e) => set({ brandColor: e.target.value.toUpperCase() })} className="h-6 w-6 cursor-pointer border-0 bg-transparent p-0" />
                          <input
                            aria-label="Colour code"
                            value={hexText}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              if (!/^#?[0-9a-f]{0,6}$/i.test(v)) return;
                              const hex = v.startsWith("#") ? v : `#${v}`;
                              setHexText(hex);
                              if (hex.length === 7) set({ brandColor: hex.toUpperCase() });
                            }}
                            className="w-[76px] bg-transparent font-mono text-xs text-gray-800 outline-none"
                          />
                        </label>
                      </div>
                    </Row>
                    <Row label="Button text">
                      <Seg value={look.buttonText} options={[["white", "White"], ["black", "Black"]]} onChange={(v) => set({ buttonText: v })} small />
                    </Row>
                    <Row label="Background">
                      <Seg value={look.background} options={[["site", "Website's own"], ["white", "White"], ["custom", "Custom"]]} onChange={(v) => set({ background: v })} small />
                      {look.background === "custom" && (
                        <label className="mt-1 flex w-max items-center gap-2 rounded-xl border border-gray-200 px-2 py-1">
                          <input type="color" aria-label="Background colour" value={look.backgroundColor} onChange={(e) => set({ backgroundColor: e.target.value.toUpperCase() })} className="h-6 w-6 cursor-pointer border-0 bg-transparent p-0" />
                          <span className="font-mono text-xs text-gray-800">{look.backgroundColor}</span>
                        </label>
                      )}
                    </Row>
                  </Section>

                  <Section title="Shape & text">
                    <Row label="Corners">
                      <Seg value={look.corners} options={CORNERS} onChange={(v) => set({ corners: v })} />
                    </Row>
                    <Row label="Font" hint={look.font === "site" ? "Uses your website's font when it's a common or Google font." : ""}>
                      <select value={look.font} onChange={(e) => set({ font: e.target.value })} className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900">
                        {FONTS.map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </Row>
                    <Row label={`Button label · ${current.label}`}>
                      <input
                        value={look.labels?.[block] || ""}
                        placeholder={DEFAULT_LABEL[block]}
                        onChange={(e) => setLabel(block, e.target.value)}
                        maxLength={24}
                        className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900"
                      />
                    </Row>
                    <Row label="Language">
                      <Seg value={look.language} options={LANGS} onChange={(v) => set({ language: v })} small />
                    </Row>
                  </Section>

                  {block === "shop" && (
                    <Section title="Products">
                      {products === null ? (
                        <div className="h-20 animate-pulse rounded-xl bg-gray-100" />
                      ) : ordered.length === 0 ? (
                        <p className="text-sm text-gray-600">No passes or subscriptions on sale yet. Add them in My Products.</p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {ordered.map((p, i) => {
                            const id = String(p._id);
                            const shown = !(look.hiddenProducts || []).includes(id);
                            const d = describe(p);
                            return (
                              <li key={id} className={classNames("flex items-center gap-2 rounded-xl border px-2.5 py-2", look.highlightProductId === id ? "border-[#481B3A] bg-[#F7F1F6]" : "border-gray-200")}>
                                <span className="flex flex-col">
                                  <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(id, -1)} className="h-4 text-[10px] leading-none text-gray-500 disabled:opacity-30">▲</button>
                                  <button type="button" aria-label="Move down" disabled={i === ordered.length - 1} onClick={() => move(id, 1)} className="h-4 text-[10px] leading-none text-gray-500 disabled:opacity-30">▼</button>
                                </span>
                                <button
                                  type="button"
                                  title="Highlight as Most popular"
                                  onClick={() => set({ highlightProductId: look.highlightProductId === id ? "" : id })}
                                  disabled={!shown}
                                  className={classNames("min-w-0 flex-1 text-left", !shown && "opacity-50")}
                                >
                                  <span className="block truncate text-sm font-bold text-gray-900">{p.productName}</span>
                                  <span className="block truncate text-xs text-gray-500">
                                    {d.priceMain}{d.kind === "sub" ? ` ${d.priceSub}` : ""}{look.highlightProductId === id ? " · Most popular" : ""}
                                  </span>
                                </button>
                                <Toggle checked={shown} onChange={() => toggleShown(id)} label={`Show ${p.productName}`} />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      <p className="text-xs text-gray-500">Arrows change the order. Click a product to mark it "Most popular".</p>
                      <Check checked={look.showPerClass} onChange={(v) => set({ showPerClass: v })}>Show price per class</Check>
                      <Check checked={look.showValidity} onChange={(v) => set({ showValidity: v })}>Show how long a pass is valid</Check>
                    </Section>
                  )}

                  <Section title="Also">
                    <Check checked={look.poweredBy} onChange={(v) => set({ poweredBy: v })}>Show "Powered by FitClub"</Check>
                    <Check checked={look.stripeColor} onChange={(v) => set({ stripeColor: v })}>Use the brand colour on the payment page</Check>
                    <button type="button" onClick={() => setLook({ ...DEFAULT_LOOK, productOrder: look.productOrder, hiddenProducts: look.hiddenProducts, highlightProductId: look.highlightProductId })} className="self-start text-xs font-bold text-gray-500 underline">
                      Reset colours and shape
                    </button>
                  </Section>
                </div>

                {/* ---------- preview + snippet ---------- */}
                <div className="flex min-w-0 flex-col gap-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex gap-1 rounded-full bg-[#EDE5F0] p-1 text-sm font-extrabold">
                      {[["desktop", "Desktop"], ["mobile", "Mobile"]].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setDevice(v)} className={classNames("rounded-full px-3 py-1.5", device === v ? "bg-white shadow" : "text-gray-600")}>{l}</button>
                      ))}
                    </div>
                    {block === "shop" && (
                      <div className="flex gap-1 rounded-full bg-[#EDE5F0] p-1 text-xs font-extrabold">
                        {[["products", "Products"], ["details", "Your details"], ["done", "Done"]].map(([v, l]) => (
                          <button key={v} type="button" onClick={() => setStep(v)} className={classNames("rounded-full px-3 py-1.5", step === v ? "bg-white shadow" : "text-gray-600")}>{l}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div
                    className="overflow-hidden rounded-3xl border border-dashed border-gray-300 p-4 sm:p-6"
                    style={{ background: look.background === "site" ? "repeating-conic-gradient(#F4F1F5 0% 25%, #FBFAFB 0% 50%) 50% / 22px 22px" : "#F7F4F8" }}
                  >
                    <p className="mb-3 text-xs font-bold text-gray-500">
                      Preview{look.background === "site" ? " · the checked background stands for your website" : ""} · buying is switched off
                    </p>
                    <div className={classNames("mx-auto transition-all", device === "mobile" ? "max-w-[390px]" : "max-w-full")}>
                      <iframe
                        key={previewSrc}
                        ref={frameRef}
                        title="Preview"
                        src={previewSrc}
                        onLoad={postLook}
                        className="pointer-events-none block w-full border-0 bg-transparent"
                        style={{ height: block === "join" ? 64 : frameHeight }}
                      />
                    </div>
                  </div>

                  <div className="rounded-3xl border border-gray-200 bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[17px] font-black text-gray-900">Put it on your website</p>
                        <p className="text-sm text-gray-600">
                          Paste it in an "Embed code" or "Custom HTML" block (Wix, WordPress, Squarespace, Webflow…). Saved changes show up there automatically.
                        </p>
                      </div>
                      <div className="flex gap-1 rounded-full bg-[#F1EEF2] p-1 text-xs font-extrabold">
                        {[["inline", "In the page"], ["button", "As a button"], ["link", "Just the link"]]
                          .filter(([v]) => v !== "inline" || current.inline)
                          .map(([v, l]) => (
                            <button key={v} type="button" onClick={() => setSnippetMode(v)} className={classNames("rounded-full px-3 py-1.5", snippetMode === v ? "bg-white shadow" : "text-gray-500")}>{l}</button>
                          ))}
                      </div>
                    </div>
                    {!current.inline && snippetMode !== "link" && (
                      <p className="mt-2 text-xs text-gray-500">Joining signs the new member in, so this block is a button that opens your sign-up page.</p>
                    )}
                    <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all rounded-2xl bg-[#1D1320] p-4 text-[13px] leading-relaxed text-[#F3E9FF]">{snippet}</pre>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button type="button" onClick={copy} className="h-11 rounded-2xl bg-brown-coffee px-5 text-sm font-extrabold text-white hover:bg-light-brown-coffee">
                        {snippetMode === "link" ? "Copy link" : "Copy code"}
                      </button>
                      <a
                        href={`mailto:?subject=${encodeURIComponent(`FitClub on our website: ${current.label}`)}&body=${encodeURIComponent(mailBody)}`}
                        className="flex h-11 items-center rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50"
                      >
                        Email it to my web designer
                      </a>
                      {snippetMode === "link" && (
                        <a href={snippet} target="_blank" rel="noopener noreferrer" className="flex h-11 items-center rounded-2xl border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-900 hover:bg-gray-50">
                          Open ↗
                        </a>
                      )}
                    </div>
                    {dirty && <p className="mt-3 text-xs font-bold text-[#7A4A00]">You have unsaved changes: save the look to show them on your website.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      }
    />
  );
}
